import { KeyRound, ShieldCheck, Users } from '@orgus/ui/icons';
import { useNavigate } from 'react-router';
import { Badge, Button, Skeleton } from '@orgus/ui';
import { SETTINGS_SECTIONS } from '../../settings/registry.ts';
import { useOnboarding } from '../client.ts';
import { Callout, Code, Instruction } from '../parts.tsx';

const VIA_LABEL = { 'owner-link': 'the owner link', github: 'GitHub', token: 'an API token' } as const;

/** Step 1: you are signed in; how other people (and your phone) sign in. */
export function SignInStep() {
  const navigate = useNavigate();
  const auth = useOnboarding((s) => s.probes.auth);
  const hasMembers = SETTINGS_SECTIONS.some((s) => s.id === 'members');

  if (!auth) {
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Checking how this hub signs people in">
        <Skeleton className="h-14" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  const who = auth.me ? (auth.me.name ?? auth.me.id) : 'the owner';
  const via = auth.me ? VIA_LABEL[auth.me.via] : VIA_LABEL['owner-link'];

  return (
    <div className="flex flex-col gap-4">
      <Callout tone="ok" title={`You are signed in as ${who}`}>
        With {via}. Everything you do here is checked against your role and written to the audit log.
      </Callout>

      <div className="flex flex-col gap-3">
        <h4 className="flex items-center gap-2 text-ui font-medium text-fg">
          <Users className="size-4 text-fg-subtle" aria-hidden />
          How other people sign in
        </h4>
        {auth.installed && auth.githubConfigured ? (
          <Callout
            title={
              <span className="flex items-center gap-2">
                Sign in with GitHub is on <Badge tone="passed" dot>Ready</Badge>
              </span>
            }
            action={
              hasMembers ? (
                <Button size="sm" onClick={() => navigate('/settings/members')}>
                  Manage members
                </Button>
              ) : undefined
            }
          >
            People you allow sign in with their GitHub account, each with a role: viewer, operator, admin or owner.
          </Callout>
        ) : auth.installed ? (
          <ol className="flex flex-col gap-3">
            <Instruction n={1} title="Register a GitHub OAuth app (only you can do this)">
              <p>
                On GitHub open Settings, Developer settings, OAuth Apps, New OAuth App. Use <Code>{window.location.origin}</Code> as the homepage and{' '}
                <Code wrap>{`${window.location.origin}/api/auth/github/callback`}</Code> as the callback, and tick Enable Device Flow.
              </p>
            </Instruction>
            <Instruction n={2} title="Give the hub its client id and secret">
              <p>
                Put them in <Code>~/.orgus/hub/auth.json</Code> with the GitHub users or organisations you allow. The hub reads the file again by itself, no restart.
              </p>
            </Instruction>
            <Instruction n={3} title="Choose who may sign in">
              {hasMembers ? (
                <div>
                  <Button size="sm" icon={<ShieldCheck />} onClick={() => navigate('/settings/members')}>
                    Open Members
                  </Button>
                </div>
              ) : (
                <p>Add GitHub users and organisations with a role in auth.json.</p>
              )}
            </Instruction>
          </ol>
        ) : (
          <Callout title="This hub signs people in with the owner link">
            Anyone with the link the hub printed when it started is the owner. Run <Code>orgus token</Code> on the hub machine for a fresh link; older links stop working. Sign in with
            GitHub, with roles for each person, arrives with the auth module.
          </Callout>
        )}
      </div>

      <div className="flex items-start gap-3 rounded-control border border-border-subtle px-3 py-3 text-ui text-fg-muted">
        <KeyRound className="mt-1 size-4 shrink-0 text-fg-subtle" aria-hidden />
        <p>
          On your phone you sign in the same way: with GitHub{auth.githubConfigured ? '' : ' once it is set up'}, or with a link from <Code>orgus token</Code> opened at the
          phone's address. Step 5 covers it.
        </p>
      </div>
    </div>
  );
}
